import React, { useEffect, useMemo, useState } from "react";
import API from "../services/api";
import { resolveProfilePhoto as resolveAssetUrl } from "../utils/profilePhoto";
import "./ProductGalleryPage.css";

const normalizeWeightLabel = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/kg/gi, "KG")
    .replace(/gram/gi, "Gram")
    .replace(/g\b/gi, "Gram")
    .replace(/quintal/gi, "Quintal")
    .replace(/ton/gi, "Ton");

const ProductGalleryPage = ({ endpoint, title = "Product Gallery", subtitle = "Packaging-wise product images" }) => {
  const [products, setProducts] = useState([]);
  const [selectedWeight, setSelectedWeight] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [touchStartX, setTouchStartX] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const { data } = await API.get(endpoint);
        setProducts(Array.isArray(data) ? data : []);
        setError("");
      } catch (err) {
        console.error("Failed to load product gallery:", err);
        setError("Gallery load nahi ho payi.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [endpoint]);

  const galleryItems = useMemo(
    () =>
      products.flatMap((product) =>
        (product.galleryImages || []).map((image) => ({
          id: image._id,
          productId: product._id,
          productName: product.name,
          description: product.description || "",
          imageUrl: image.imageUrl,
          caption: image.caption || product.name,
          isPrimary: !!image.isPrimary,
          packagingWeights: (image.packagingWeights || []).map(normalizeWeightLabel),
        }))
      ),
    [products]
  );

  const weightOptions = useMemo(() => {
    const options = new Set(["all"]);
    galleryItems.forEach((item) => {
      item.packagingWeights.forEach((weight) => options.add(weight));
    });
    return [...options];
  }, [galleryItems]);

  const filteredItems = useMemo(() => {
    if (selectedWeight === "all") return galleryItems;
    return galleryItems.filter((item) => item.packagingWeights.includes(selectedWeight));
  }, [galleryItems, selectedWeight]);

  useEffect(() => {
    if (!filteredItems.length) {
      setLightboxIndex(-1);
      return;
    }
    if (lightboxIndex >= filteredItems.length) {
      setLightboxIndex(filteredItems.length - 1);
    }
  }, [filteredItems, lightboxIndex]);

  const lightboxItem =
    lightboxIndex >= 0 && lightboxIndex < filteredItems.length
      ? filteredItems[lightboxIndex]
      : null;

  const openLightbox = (index) => {
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(-1);
    setTouchStartX(null);
  };

  const showPrev = () => {
    if (!filteredItems.length) return;
    setLightboxIndex((prev) => (prev <= 0 ? filteredItems.length - 1 : prev - 1));
  };

  const showNext = () => {
    if (!filteredItems.length) return;
    setLightboxIndex((prev) => (prev >= filteredItems.length - 1 ? 0 : prev + 1));
  };

  const handleLightboxTouchStart = (event) => {
    setTouchStartX(event.touches?.[0]?.clientX ?? null);
  };

  const handleLightboxTouchEnd = (event) => {
    const endX = event.changedTouches?.[0]?.clientX ?? null;
    if (touchStartX === null || endX === null) return;
    const delta = endX - touchStartX;
    if (Math.abs(delta) < 40) return;
    if (delta > 0) showPrev();
    else showNext();
    setTouchStartX(null);
  };

  useEffect(() => {
    if (lightboxIndex < 0) return undefined;
    const total = filteredItems.length;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeLightbox();
      if (event.key === "ArrowLeft") {
        setLightboxIndex((prev) => (prev <= 0 ? total - 1 : prev - 1));
      }
      if (event.key === "ArrowRight") {
        setLightboxIndex((prev) => (prev >= total - 1 ? 0 : prev + 1));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxIndex, filteredItems.length]);

  if (loading) {
    return <div className="pg-state">Loading gallery...</div>;
  }

  if (error) {
    return <div className="pg-state pg-state-error">{error}</div>;
  }

  return (
    <div className="pg-page">
      <div className="pg-hero">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        <div className="pg-count">{filteredItems.length} images</div>
      </div>

      <div className="pg-filters">
        {weightOptions.map((weight) => (
          <button
            key={weight}
            type="button"
            className={`pg-filter ${selectedWeight === weight ? "active" : ""}`}
            onClick={() => setSelectedWeight(weight)}
          >
            {weight === "all" ? "All" : weight}
          </button>
        ))}
      </div>

      {filteredItems.length === 0 ? (
        <div className="pg-empty">
          {galleryItems.length === 0
            ? "Abhi tak admin ne koi gallery image upload nahi ki."
            : "Is weight ke liye abhi koi image available nahi hai."}
        </div>
      ) : (
        <div className="pg-grid">
          {filteredItems.map((item) => (
            <article key={item.id} className="pg-tile">
              <button
                type="button"
                className="pg-tile-hit"
                onClick={() => openLightbox(filteredItems.findIndex((entry) => entry.id === item.id))}
              >
              <div className="pg-tile-image-wrap">
                <img
                  className="pg-tile-image"
                  src={resolveAssetUrl(item.imageUrl)}
                  alt={item.caption}
                />
              </div>
              <div className="pg-tile-overlay">
                <div className="pg-card-top">
                  <div className="pg-product-name">{item.productName}</div>
                  {item.isPrimary ? <span className="pg-tag">Front</span> : null}
                </div>
                <div className="pg-caption">{item.caption}</div>
                {item.description ? <div className="pg-description">{item.description}</div> : null}
                <div className="pg-tags">
                  {(item.packagingWeights || []).length === 0 ? (
                    <span className="pg-tag muted">All packaging</span>
                  ) : (
                    item.packagingWeights.map((weight) => (
                      <span key={`${item.id}-${weight}`} className="pg-tag">
                        {weight}
                      </span>
                    ))
                  )}
                </div>
              </div>
              </button>
            </article>
          ))}
        </div>
      )}

      {lightboxItem ? (
        <div className="pg-lightbox-backdrop" onClick={closeLightbox}>
          <div
            className="pg-lightbox"
            onClick={(event) => event.stopPropagation()}
            onTouchStart={handleLightboxTouchStart}
            onTouchEnd={handleLightboxTouchEnd}
          >
            <button type="button" className="pg-lightbox-close" onClick={closeLightbox}>
              ×
            </button>
            {filteredItems.length > 1 ? (
              <button type="button" className="pg-lightbox-nav pg-lightbox-prev" onClick={showPrev}>
                ‹
              </button>
            ) : null}
            <div className="pg-lightbox-image-wrap">
              <img
                className="pg-lightbox-image"
                src={resolveAssetUrl(lightboxItem.imageUrl)}
                alt={lightboxItem.caption}
              />
            </div>
            {filteredItems.length > 1 ? (
              <button type="button" className="pg-lightbox-nav pg-lightbox-next" onClick={showNext}>
                ›
              </button>
            ) : null}
            <div className="pg-lightbox-info">
              <div className="pg-card-top">
                <div className="pg-product-name">{lightboxItem.productName}</div>
                {lightboxItem.isPrimary ? <span className="pg-tag">Front</span> : null}
              </div>
              <div className="pg-caption">{lightboxItem.caption}</div>
              {lightboxItem.description ? <div className="pg-description">{lightboxItem.description}</div> : null}
              <div className="pg-tags">
                {(lightboxItem.packagingWeights || []).length === 0 ? (
                  <span className="pg-tag muted">All packaging</span>
                ) : (
                  lightboxItem.packagingWeights.map((weight) => (
                    <span key={`${lightboxItem.id}-lightbox-${weight}`} className="pg-tag">
                      {weight}
                    </span>
                  ))
                )}
              </div>
              <div className="pg-lightbox-count">
                {lightboxIndex + 1} / {filteredItems.length}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ProductGalleryPage;
